/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { TMyHubType, type TCarouselItem } from "../../../model/types";
import { Locale } from "../../../model/locale/Locale";
import { LocaleKeys } from "../../../model/locale/LocaleKeys";
import { DateTime } from "../../../model/utility/DateTime";
import { TilePreviewTemplate } from "./TilePreviewTemplate";
import { CarouselTemplate } from "../../../model/enums";

/**
 * Formatted preview data for a live content tile.
 */
export interface TilePreviewData {
  /** Line 1: Content title */
  title: string;
  /** Line 2: Subtitle info — for movies: "2024 · TVG"; for episodes: SmartTV "S5 :E14 - Episode Title", Browser "S5 E14 - Episode Title" */
  subtitleInfo?: string;
  /** Line 3a: Channel info (e.g. "HBO(122)"). Not shown for movies. */
  channelInfo?: string;
  /** Line 3b: Time range (e.g. "Today · 6:00-7:30pm"). Not shown for movies. */
  timeRange?: string;
  /** Line 4 (browser only): Day label and time range combined (e.g. "Today · 6:00-7:30pm"). */
  dayAndTimeRange?: string;
  /** Line 3: Full channel and time range */
  metaInfo?: string;
}

/**
 * Parses airing times into separate day-label and time-range strings.
 * - dayLabel:  "Today" or "Tue 3/6"
 * - timeRange: "6:00-7:30pm" or "6:00pm"
 */
const formatAiringTime = (
  startTime?: string,
  endTime?: string
): { dayLabel: string; timeRange: string } => {
  if (!startTime) return { dayLabel: "", timeRange: "" };

  const start = new Date(startTime);
  if (Number.isNaN(start.getTime())) return { dayLabel: "", timeRange: "" };

  const now = new Date();
  const isToday =
    start.getFullYear() === now.getFullYear() &&
    start.getMonth() === now.getMonth() &&
    start.getDate() === now.getDate();

  const dayLabel = isToday
    ? Locale.getString(LocaleKeys.HOME_TILE_PREVIEW_TODAY)
    : `${start.toLocaleDateString("en-US", { weekday: "short" })} ${start.getMonth() + 1}/${start.getDate()}`;

  const endDate = endTime ? new Date(endTime) : null;

  const startStr = new DateTime(start).to12HourClock();
  const endStr = endDate ? new DateTime(endDate).to12HourClock() : "";
  const timeRange = endStr ? `${startStr} - ${endStr}` : startStr;

  return { dayLabel, timeRange };
};

/**
 * Builds the subtitle info string (Line 2).
 * - Movie:   "2024 · TVG"
 * - Episode: "S5 :E14 - Episode Title"
 *
 * @param {TCarouselItem} item - The carousel item
 * @returns {string} Formatted subtitle info or empty string if no data available
 */
function buildSubtitleInfo(
  item: TCarouselItem,
  isBrowser: boolean = false
): string {
  if (item.contentType === "movie") {
    const parts: string[] = [];

    if (item.movieYear !== undefined) {
      parts.push(String(item.movieYear));
    }

    if (item.ratingBadge !== undefined && item.ratingBadge !== "") {
      parts.push(item.ratingBadge);
    }

    return parts.join(" · ");
  }

  const parts: string[] = [];

  if (item.seasonNumber !== undefined && item.episodeNumber !== undefined) {
    const episodeSeparator: string = isBrowser ? " E" : " :E";
    parts.push(
      "S" +
        String(item.seasonNumber) +
        episodeSeparator +
        String(item.episodeNumber)
    );
  }

  if (item.episodeTitle !== undefined && item.episodeTitle !== "") {
    if (parts.length > 0) {
      parts.push("- " + item.episodeTitle);
    } else {
      parts.push(item.episodeTitle);
    }
  }

  return parts.join(" ");
}

/**
 * Builds the channel and time string (Line 3) for live content, excluding
 * the rating badge which is rendered as a separate styled element by the component.
 * Format: "HBO(122) · Today · 6:00-7:30pm"
 *
 * @param {TCarouselItem} item - The carousel item
 * @returns {string} Formatted channel and time string
 */
function buildChannelAndTime(item: TCarouselItem): string {
  const parts: string[] = [];

  if (item.channelName !== undefined && item.channelName !== "") {
    let channelDisplay: string = item.channelName;
    if (item.channelNumber !== undefined && item.channelNumber !== "") {
      channelDisplay = item.channelName + "(" + item.channelNumber + ")";
    }
    parts.push(channelDisplay);
  }

  const hasStartTime: boolean =
    item.startTime !== undefined && item.startTime !== "";
  const hasEndTime: boolean = item.endTime !== undefined && item.endTime !== "";

  if (hasStartTime && hasEndTime) {
    const { dayLabel, timeRange: startTimeRange } = formatAiringTime(
      item.startTime as string
    );
    const { timeRange: endTimeRange } = formatAiringTime(
      item.endTime as string
    );

    if (dayLabel !== "") {
      parts.push(dayLabel);
    }

    if (startTimeRange !== "" && endTimeRange !== "") {
      parts.push(startTimeRange + "-" + endTimeRange);
    }
  }

  return parts.join(" · ");
}

/**
 * Formats data for the BROWSER_LANDSCAPE_HORIZONTAL template (4-line browser layout).
 * Includes title, subtitle (isBrowser episode separator), channel info, and day & time range.
 */
function formatForBrowserLandscapeHorizontalPreview(
  item: TCarouselItem
): TilePreviewData {
  const channelAndTime: string = buildChannelAndTime(item);
  let channelInfo: string | undefined;
  let timeRange: string | undefined;
  let dayAndTimeRange: string | undefined;

  if (channelAndTime !== "") {
    const parts: string[] = channelAndTime.split(" · ");
    if (parts.length >= 2) {
      channelInfo = parts[0];
      timeRange = " · " + parts.slice(1).join(" · ");
      dayAndTimeRange = parts.slice(1).join(" · ");
    } else {
      channelInfo = channelAndTime;
    }
  }

  return {
    title: item.title,
    subtitleInfo: buildSubtitleInfo(item, true),
    channelInfo,
    timeRange,
    dayAndTimeRange,
    metaInfo: channelAndTime !== "" ? channelAndTime : undefined
  };
}

/**
 * Formats data for the PIP_LANDSCAPE_PREVIEW template (recently watched channel, SmartTV).
 * Title and subtitle only — no channel or time meta.
 */
function formatForPipLandscapePreview(item: TCarouselItem): TilePreviewData {
  return {
    title: item.title,
    subtitleInfo: buildSubtitleInfo(item, false)
  };
}

/**
 * Formats data for the LIVE_LANDSCAPE_HORIZONTAL_PREVIEW template (SmartTV 3-line live layout).
 * Includes title, subtitle, channel info, and time range.
 */
function formatForLiveLandscapeHorizontalPreview(
  item: TCarouselItem
): TilePreviewData {
  const channelAndTime: string = buildChannelAndTime(item);
  let channelInfo: string | undefined;
  let timeRange: string | undefined;

  if (channelAndTime !== "") {
    const parts: string[] = channelAndTime.split(" · ");
    if (parts.length >= 2) {
      channelInfo = parts[0];
      timeRange = " · " + parts.slice(1).join(" · ");
    } else {
      channelInfo = channelAndTime;
    }
  }

  return {
    title: item.title,
    subtitleInfo: buildSubtitleInfo(item, false),
    channelInfo,
    timeRange,
    metaInfo: channelAndTime !== "" ? channelAndTime : undefined
  };
}

/**
 * Formats data for the NON_LIVE_LANDSCAPE_HORIZONTAL_PREVIEW template.
 * TODO: Differentiate subtitle and meta formatting from LIVE_LANDSCAPE_HORIZONTAL_PREVIEW
 *       once non-live specific layout requirements are defined.
 */
function formatForNonLiveLandscapeHorizontalPreview(
  item: TCarouselItem
): TilePreviewData {
  return formatForLiveLandscapeHorizontalPreview(item);
}

/**
 * Formats data for the MOVIE_POSTER_HORIZONTAL template (Top 10 For You — movie).
 * Subtitle format: "Movie · 2024 · TV-G"
 */
function formatForMoviePosterHorizontal(item: TCarouselItem): TilePreviewData {
  const parts: string[] = [
    Locale.getString(LocaleKeys.HOME_TILE_PREVIEW_TYPE_MOVIE)
  ];
  if (item.movieYear !== undefined) {
    parts.push(String(item.movieYear));
  }
  if (item.ratingBadge !== undefined && item.ratingBadge !== "") {
    parts.push(item.ratingBadge);
  }
  return {
    title: item.title,
    subtitleInfo: parts.join(" · ")
  };
}

/**
 * Formats data for the SHOW_POSTER_HORIZONTAL template (Top 10 For You — show/episode).
 * Subtitle format: "Show · TV-G"
 */
function formatForShowPosterHorizontal(item: TCarouselItem): TilePreviewData {
  let subtitleInfo: string;
  if (item.contentType === "person") {
    subtitleInfo = "";
  } else {
    const parts: string[] = [
      Locale.getString(LocaleKeys.HOME_TILE_PREVIEW_TYPE_SHOW)
    ];
    if (item.ratingBadge !== undefined && item.ratingBadge !== "") {
      parts.push(item.ratingBadge);
    }
    subtitleInfo = parts.join(" · ");
  }
  return {
    title: item.title,
    subtitleInfo: subtitleInfo
  };
}

/**
 * Formats data for the MOVIE_RECORDING_HORIZONTAL template (My Hub — Recorded/Scheduled tab).
 * Subtitle format: "duration of recording · TV-G"
 */
function formatForMovieRecordingHorizontalPreview(
  item: TCarouselItem
): TilePreviewData {
  const parts: string[] = [];
  if (item.duration !== undefined) {
    parts.push(String(DateTime.convertToReadable(item.duration * 1000)));
  }
  if (item.ratingBadge !== undefined && item.ratingBadge !== "") {
    parts.push(item.ratingBadge);
  }
  return {
    title: item.title,
    subtitleInfo: parts.join(" · ")
  };
}

/**
 * Formats data for the SERIES_RECORDING_HORIZONTAL template (My Hub — Recorded/Scheduled tab).
 * Subtitle format: "X Recordings"
 */
function formatForSeriesRecordingHorizontalPreview(
  item: TCarouselItem
): TilePreviewData {
  const recordingCount: number | undefined = item.recordingCount;
  const recordingCountStr: string =
    recordingCount !== undefined && recordingCount > 0
      ? Locale.getString(LocaleKeys.NO_OF_RECORDINGS).replace(
          "{{recordings}}",
          String(recordingCount)
        )
      : "";
  return {
    title: item.title,
    subtitleInfo: recordingCountStr
  };
}
/**
 * Formats data for the LIVE_PIP_LANDSCAPE_PREVIEW template (Now Playing PIP).
 * Includes channel info for live content; subtitle may be overridden by the caller.
 */
function formatForLivePipLandscapePreview(
  item: TCarouselItem
): TilePreviewData {
  const channelAndTime: string = buildChannelAndTime(item);
  const channelInfo: string | undefined =
    channelAndTime !== "" ? channelAndTime.split(" · ")[0] : undefined;
  return {
    title: item.title,
    subtitleInfo: channelInfo
  };
}

/**
 * Formats a TCarouselItem into structured preview data based on the given template.
 * Each template encodes a specific visual layout and data selection strategy.
 *
 * @param {TCarouselItem} item - The carousel item to format
 * @param {TilePreviewTemplate} template - The layout template to apply
 * @returns {TilePreviewData} Formatted preview data for the given template
 */
export function formatTilePreview(
  item: TCarouselItem,
  template: TilePreviewTemplate
): TilePreviewData {
  switch (template) {
    case TilePreviewTemplate.BROWSER_LANDSCAPE_HORIZONTAL_PREVIEW:
      return formatForBrowserLandscapeHorizontalPreview(item);
    case TilePreviewTemplate.LOGO_PIP_LANDSCAPE_PREVIEW:
      return formatForPipLandscapePreview(item);
    case TilePreviewTemplate.LIVE_LANDSCAPE_HORIZONTAL_PREVIEW:
      return formatForLiveLandscapeHorizontalPreview(item);
    case TilePreviewTemplate.NON_LIVE_LANDSCAPE_HORIZONTAL_PREVIEW:
      return formatForNonLiveLandscapeHorizontalPreview(item);
    case TilePreviewTemplate.MOVIE_POSTER_HORIZONTAL:
      return formatForMoviePosterHorizontal(item);
    case TilePreviewTemplate.SHOW_POSTER_HORIZONTAL:
      return formatForShowPosterHorizontal(item);
    case TilePreviewTemplate.LIVE_PIP_LANDSCAPE_PREVIEW:
      return formatForLivePipLandscapePreview(item);
    case TilePreviewTemplate.SERIES_RECORDING_HORIZONTAL:
      return formatForSeriesRecordingHorizontalPreview(item);
    case TilePreviewTemplate.MOVIE_RECORDING_HORIZONTAL:
      return formatForMovieRecordingHorizontalPreview(item);
    default:
      return formatForLiveLandscapeHorizontalPreview(item);
  }
}

export const resolveMyHubTilePreviewTemplate = (
  item: TCarouselItem,
  carouselTemplate: CarouselTemplate,
  isSmartTV: boolean
): TilePreviewTemplate => {
  const isPosterCarousel: boolean =
    carouselTemplate === CarouselTemplate.POSTER_HORIZONTAL;

  if (isPosterCarousel) {
    // A folder groups several recordings under one series, so "N recordings"
    // is still the right line for it everywhere — browser included — the
    // same as SERIES_RECORDING_HORIZONTAL already reads on TV below. Only an
    // actual recording tile (a single episode or movie) switches to a poster
    // read in the browser; the "N recordings" / duration line TV shows
    // instead for it is a TV-only affordance.
    const isFolder: boolean = item.myHubItemType === TMyHubType.FOLDER;
    if (!isSmartTV && !isFolder) {
      return item.contentType === "movie"
        ? TilePreviewTemplate.MOVIE_POSTER_HORIZONTAL
        : TilePreviewTemplate.SHOW_POSTER_HORIZONTAL;
    }
    if (isSmartTV && item.contentType === "movie") {
      return TilePreviewTemplate.MOVIE_RECORDING_HORIZONTAL;
    }
  }
  return TilePreviewTemplate.SERIES_RECORDING_HORIZONTAL;
};
/**
 * Determines which TilePreviewTemplate to render for a carousel item.
 */
export const resolveTilePreviewTemplate = (
  item: TCarouselItem,
  carouselTemplate: CarouselTemplate,
  isSmartTV: boolean
): TilePreviewTemplate => {
  const isLogoPipLandscapeCarousel: boolean =
    carouselTemplate === CarouselTemplate.LOGO_PIP_LANDSCAPE_HORIZONTAL;
  const isPosterCarousel: boolean =
    carouselTemplate === CarouselTemplate.POSTER_HORIZONTAL;

  if (isLogoPipLandscapeCarousel) {
    return TilePreviewTemplate.LOGO_PIP_LANDSCAPE_PREVIEW;
  }

  if (isPosterCarousel) {
    if (item.contentType === "movie") {
      return TilePreviewTemplate.MOVIE_POSTER_HORIZONTAL;
    } else {
      return TilePreviewTemplate.SHOW_POSTER_HORIZONTAL;
    }
  }
  // Browser template
  if (!isSmartTV) {
    return TilePreviewTemplate.BROWSER_LANDSCAPE_HORIZONTAL_PREVIEW;
  }

  return TilePreviewTemplate.LIVE_LANDSCAPE_HORIZONTAL_PREVIEW;
};
